import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import Field from '../../../common/components/field';
import { IconClose } from '../../../common/icons';
import { FARM_NAME_MAX, FARM_RESPONSIBLE_MAX } from '../../../core/constants/forms';
import { useCreateFarm, useUpdateFarm } from '../contexts/farm-mutations';

import '../styles/farm-form.css';

// TSS vite/03 — alta/edición de una finca del cliente (public-farms.md).
// Modal role="dialog" (espejo del farm-form del dashboard). Copia el patrón de
// mutación+form de perfil: validación local → mutación → err.fields por campo
// + toast. NUNCA envía owner_id/status (el backend los descarta).
//
// location es JSON libre; el portal solo edita `address` (contract §Fuera de
// alcance: sin geolocalización estructurada).

const addressOf = (loc) => {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    return loc.address || '';
};

export default function FarmForm({ farm, onClose, onSaved }) {
    const editing = !!farm;
    const create = useCreateFarm();
    const update = useUpdateFarm(farm?.id);
    const busy = create.isPending || update.isPending;

    const [name, setName] = useState(farm?.name ?? '');
    const [responsible, setResponsible] = useState(farm?.responsible ?? '');
    const [size, setSize] = useState(farm?.size != null ? String(farm.size) : '');
    const [address, setAddress] = useState(addressOf(farm?.location));
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [busy, onClose]);

    const trimmedName = name.trim();

    // Validación cliente (el backend revalida y responde 400 { fields }).
    const localError = useMemo(() => {
        if (!trimmedName) return { name: 'El nombre es obligatorio.' };
        if (trimmedName.length > FARM_NAME_MAX) return { name: `Máximo ${FARM_NAME_MAX} caracteres.` };
        if (responsible.trim().length > FARM_RESPONSIBLE_MAX) {
            return { responsible: `Máximo ${FARM_RESPONSIBLE_MAX} caracteres.` };
        }
        if (size !== '' && !(Number(size) > 0)) {
            return { size: 'El tamaño debe ser un número mayor a 0 (o vacío).' };
        }
        return null;
    }, [trimmedName, responsible, size]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (localError || busy) return;

        // Subconjunto documentado del portal. Enviamos todos los campos editables
        // (garantiza ≥1 para el PATCH); vacíos → null (contract admite null/"").
        const payload = {
            name: trimmedName,
            responsible: responsible.trim() || null,
            size: size !== '' ? Number(size) : null,
            location: address.trim() ? { address: address.trim() } : null,
        };

        setFieldErrors({});
        const mutation = editing ? update : create;
        mutation.mutate(payload, {
            onSuccess: () => {
                toast.success(editing ? 'Finca actualizada' : 'Finca creada');
                onSaved?.();
            },
            onError: (err) => {
                if (err?.fields && typeof err.fields === 'object') setFieldErrors(err.fields);
                if (err?.status !== 403) toast.error(err?.message || 'No se pudo guardar la finca');
            },
        });
    };

    const nameError = localError?.name || fieldErrors.name;
    const responsibleError = localError?.responsible || fieldErrors.responsible;
    const sizeError = localError?.size || fieldErrors.size;
    const addressError = fieldErrors.location;

    return (
        <div
            className="farm-form"
            role="dialog"
            aria-modal="true"
            aria-label={editing ? 'Editar finca' : 'Nueva finca'}
        >
            <div className="farm-form__overlay" onClick={busy ? undefined : onClose} aria-hidden="true" />
            <form className="farm-form__card" onSubmit={handleSubmit} noValidate>
                <header className="farm-form__head">
                    <h2 className="farm-form__title">{editing ? 'Editar finca' : 'Nueva finca'}</h2>
                    <button
                        type="button"
                        className="farm-form__close"
                        onClick={onClose}
                        disabled={busy}
                        aria-label="Cerrar"
                    >
                        <IconClose />
                    </button>
                </header>

                <div className="farm-form__body">
                    <Field
                        label="Nombre"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={nameError}
                        required
                        maxLength={FARM_NAME_MAX}
                        disabled={busy}
                        placeholder="Finca La Esperanza"
                    />
                    <Field
                        label="Encargado"
                        name="responsible"
                        value={responsible}
                        onChange={(e) => setResponsible(e.target.value)}
                        error={responsibleError}
                        maxLength={FARM_RESPONSIBLE_MAX}
                        disabled={busy}
                        placeholder="Opcional — responsable de la finca"
                    />
                    <div className="farm-form__row">
                        <Field
                            label="Tamaño (ha)"
                            name="size"
                            type="number"
                            value={size}
                            onChange={(e) => setSize(e.target.value)}
                            error={sizeError}
                            disabled={busy}
                            placeholder="Opcional"
                        />
                        <Field
                            label="Ubicación"
                            name="address"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            error={addressError}
                            disabled={busy}
                            placeholder="Opcional — dirección o vereda"
                        />
                    </div>
                </div>

                <footer className="farm-form__foot">
                    <button
                        type="button"
                        className="farm-form__btn farm-form__btn--ghost"
                        onClick={onClose}
                        disabled={busy}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="farm-form__btn farm-form__btn--primary"
                        disabled={busy || !!localError}
                    >
                        {busy ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear finca'}
                    </button>
                </footer>
            </form>
        </div>
    );
}
